import React, { useState, useEffect, useRef } from 'react';
import { FaUser, FaPaperPlane, FaSmile, FaTimes, FaGift, FaEllipsisH, FaEllipsisV, FaReply, FaTrash, FaCommentDots } from 'react-icons/fa';
import { getToken } from '../utils/auth';
import { database } from '../config/firebase';
import { ref, onValue, push, serverTimestamp, off, remove } from 'firebase/database';
import { getApiBaseUrl } from '../utils/api';
import { toast } from 'react-hot-toast';


interface Message {
    id: string;
    userId: string;
    userName: string;
    userAvatar?: string;
    content: string;
    timestamp: number;
    role?: 'admin' | 'mod' | 'user';
    isSystem?: boolean;
    replyTo?: {
        id: string;
        userName: string;
        content: string;
        role?: string;
    };
}

interface UserProfile {
    id: string;
    name: string;
    avatar?: string;
    role: string;
}

interface LiveChatSidebarProps {
    streamId: string;
    onClose: () => void;
    isMobileEmbedded?: boolean;
    chatEnabled?: boolean;
}

const LiveChatSidebar: React.FC<LiveChatSidebarProps> = ({ streamId, onClose, isMobileEmbedded = false, chatEnabled = true }) => {
    const [messages, setMessages] = useState<Message[]>([]);
    const [inputValue, setInputValue] = useState('');
    const [currentUser, setCurrentUser] = useState<UserProfile | null>(null);
    const [replyingTo, setReplyingTo] = useState<Message | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const chatRef = useRef<any>(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    // Helper to process avatar URLs
    const getFullImageUrl = (path: string | null | undefined) => {
        if (!path) return undefined;
        if (path.startsWith('http')) return path;
        const API_URL = getApiBaseUrl();
        let baseUrl = API_URL.split('/api')[0];
        if (baseUrl.endsWith('/index.php')) baseUrl = baseUrl.replace('/index.php', '');
        baseUrl = baseUrl.replace(/\/$/, '');
        return `${baseUrl}${path.startsWith('/') ? '' : '/'}${path}`;
    };

    // 1. Fetch User Profile
    useEffect(() => {
        const fetchUser = async () => {
            const token = getToken();
            if (!token) return;
            try {
                const { AuthAPI } = await import('../utils/api');
                const res = await AuthAPI.me(token);
                if (res && res.user) {
                    setCurrentUser({
                        id: res.user.id,
                        name: res.user.name,
                        avatar: getFullImageUrl(res.user.avatar),
                        role: res.user.role || 'user'
                    });
                }
            } catch (error) {
                console.error('Error fetching user for chat:', error);
            }
        };
        fetchUser();
    }, []);

    // 2. Connect to Firebase Realtime Database
    useEffect(() => {
        if (!streamId) return;

        const messagesRef = ref(database, `live_chats/${streamId}`);
        chatRef.current = messagesRef;

        const unsubscribe = onValue(messagesRef, (snapshot) => {
            const data = snapshot.val();
            if (data) {
                const loadedMessages = Object.entries(data).map(([key, value]: [string, any]) => ({
                    id: key,
                    ...value,
                }));
                // Sort by timestamp
                loadedMessages.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
                setMessages(loadedMessages);
            } else {
                setMessages([]);
            }
            // Auto scroll on new messages
            setTimeout(scrollToBottom, 100);
        });

        return () => {
            off(messagesRef);
        };
    }, [streamId]);

    const handleSendMessage = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!inputValue.trim()) return;
        if (!currentUser) {
            toast.error('Vui lòng đăng nhập để chat');
            return;
        }

        try {
            const messagesRef = ref(database, `live_chats/${streamId}`);
            const newMessage: any = {
                userId: currentUser.id,
                userName: currentUser.name,
                userAvatar: currentUser.avatar || null,
                content: inputValue.trim(),
                timestamp: serverTimestamp(),
                role: currentUser.role
            };

            if (replyingTo) {
                newMessage.replyTo = {
                    id: replyingTo.id,
                    userName: replyingTo.userName,
                    content: replyingTo.content,
                    role: replyingTo.role
                };
            }

            await push(messagesRef, newMessage);
            setInputValue('');
            setReplyingTo(null); // Clear reply state
            scrollToBottom();
        } catch (error) {
            console.error('Send message error:', error);
            toast.error('Không thể gửi tin nhắn');
        }
    };

    const handleReply = (msg: Message) => {
        setReplyingTo(msg);
        // Focus input
        const input = document.querySelector('input[type="text"]') as HTMLInputElement;
        if (input) input.focus();
    };

    const [showMenu, setShowMenu] = useState(false);

    const handleClearChat = async () => {
        if (!currentUser || currentUser.role !== 'admin') {
            toast.error('Chỉ Admin mới có quyền xoá chat');
            return;
        }
        setShowMenu(false);

        if (!window.confirm('Bạn có chắc chắn muốn xoá toàn bộ tin nhắn trong phòng chat này không? Hành động này không thể hoàn tác.')) {
            return;
        }

        try {
            const messagesRef = ref(database, `live_chats/${streamId}`);
            await remove(messagesRef);
            toast.success('Đã xoá toàn bộ tin nhắn chat');
        } catch (error) {
            console.error('Clear chat error:', error);
            toast.error('Lỗi khi xoá chat');
        }
    };

    const [activeMessageMenu, setActiveMessageMenu] = useState<string | null>(null);

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (activeMessageMenu && !(event.target as Element).closest('.message-menu-btn') && !(event.target as Element).closest('.message-menu-dropdown')) {
                setActiveMessageMenu(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [activeMessageMenu]);

    const handleDeleteMessage = async (msgId: string) => {
        if (!window.confirm('Bạn có chắc chắn muốn xoá tin nhắn này?')) return;
        try {
            const messageRef = ref(database, `live_chats/${streamId}/${msgId}`);
            await remove(messageRef);
            toast.success('Đã xoá tin nhắn');
        } catch (error) {
            console.error('Delete message error:', error);
            toast.error('Lỗi khi xoá tin nhắn');
        }
        setActiveMessageMenu(null);
    };

    return (
        <div className={`flex flex-col h-full bg-[#1f1f23] border-l border-gray-800 flex-shrink-0 animate-slideLeftIn ${isMobileEmbedded ? 'w-full border-none' : 'w-80 sm:w-96'}`}>
            {/* Header - Hide on mobile if embedded because we have the toolbar */}
            {!isMobileEmbedded && (
                <div className="h-14 min-h-[56px] px-4 flex items-center justify-between border-b border-gray-800 bg-[#1f1f23]">
                    <div className="flex items-center gap-2">
                        <span className="text-gray-300 font-semibold text-sm uppercase tracking-wide">Phòng Chat</span>
                    </div>
                    <div className="flex items-center gap-3 relative">
                        {currentUser?.role === 'admin' && (
                            <div className="relative">
                                <button
                                    className="text-gray-400 hover:text-white transition-colors"
                                    title="Cài đặt"
                                    onClick={() => setShowMenu(!showMenu)}
                                >
                                    <FaEllipsisV />
                                </button>
                                {showMenu && (
                                    <div className="absolute top-8 right-0 bg-[#2a2a2e] border border-gray-700 rounded-lg shadow-xl py-1 z-50 w-40 animate-fadeIn">
                                        <button
                                            onClick={handleClearChat}
                                            className="w-full text-left px-4 py-2 text-red-500 hover:bg-white/5 text-xs font-bold transition-all flex items-center gap-2"
                                        >
                                            <FaTimes /> Xoá tất cả
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                        <button onClick={onClose} className="flex items-center gap-1 text-gray-400 hover:text-white transition-colors text-sm font-medium">
                            <span>Ẩn chat</span>
                            <FaTimes />
                        </button>
                    </div>
                </div>
            )}



            {/* Messages List - Custom Tiny Scrollbar */}
            <style>
                {`
                .chat-scrollbar::-webkit-scrollbar {
                    width: 4px !important;
                }
                .chat-scrollbar::-webkit-scrollbar-track {
                    background: transparent !important;
                }
                .chat-scrollbar::-webkit-scrollbar-thumb {
                    background: #52525b !important;
                    border-radius: 10px !important;
                }
                `}
            </style>
            <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 chat-scrollbar flex flex-col">
                {messages.length === 0 && (
                    <div className="text-center text-gray-500 text-sm py-10">
                        Chưa có tin nhắn nào. Hãy là người đầu tiên!
                    </div>
                )}

                {messages.map((msg, index) => {
                    const isMe = currentUser && msg.userId === currentUser.id;
                    const prevMsg = index > 0 ? messages[index - 1] : null;
                    const nextMsg = index < messages.length - 1 ? messages[index + 1] : null;

                    // Grouping Logic
                    const currentDate = new Date(msg.timestamp);
                    const prevDate = prevMsg ? new Date(prevMsg.timestamp) : null;
                    const nextDate = nextMsg ? new Date(nextMsg.timestamp) : null;

                    const isSameMinute = (d1: Date, d2: Date) =>
                        d1.getFullYear() === d2.getFullYear() &&
                        d1.getMonth() === d2.getMonth() &&
                        d1.getDate() === d2.getDate() &&
                        d1.getHours() === d2.getHours() &&
                        d1.getMinutes() === d2.getMinutes();

                    let isGroupStart = true;
                    if (prevMsg && prevMsg.userId === msg.userId && prevDate) {
                        isGroupStart = !isSameMinute(currentDate, prevDate);
                    }

                    let isGroupEnd = true;
                    if (nextMsg && nextMsg.userId === msg.userId && nextDate) {
                        isGroupEnd = !isSameMinute(currentDate, nextDate);
                    }

                    // Show Avatar at the START of a group to align with Name
                    const showAvatar = !isMe && isGroupStart;

                    return (
                        <div key={msg.id} className={`group flex ${isMe ? 'justify-end' : 'justify-start'} ${isGroupStart ? 'mt-4' : 'mt-1'}`}>
                            {/* Avatar Area */}
                            {!isMe && (
                                <div className="w-8 flex-shrink-0 flex flex-col justify-start mr-2">
                                    {showAvatar ? (
                                        <div className="w-8 h-8 rounded-full bg-gray-700 overflow-hidden cursor-pointer hover:ring-2 hover:ring-blue-500 transition-all">
                                            {msg.userAvatar ? (
                                                <img src={msg.userAvatar} alt={msg.userName} className="w-full h-full object-cover" />
                                            ) : (
                                                <div className={`w-full h-full flex items-center justify-center text-xs font-bold text-white ${msg.role === 'admin' ? 'bg-red-600' :
                                                    msg.role === 'mod' ? 'bg-blue-600' :
                                                        'bg-gray-600'
                                                    }`}>
                                                    {msg.userName.charAt(0).toUpperCase()}
                                                </div>
                                            )}
                                        </div>
                                    ) : <div className="w-8" />}
                                </div>
                            )}



                            {/* Message Content */}
                            <div className={`flex flex-col max-w-[85%] ${isMe ? 'items-end' : 'items-start'}`}>
                                {/* Name (Only show for first message in group for others) */}
                                {!isMe && isGroupStart && (
                                    <div className="flex items-center gap-1.5 mb-1 ml-1">
                                        <span
                                            className={`text-xs font-bold ${msg.role === 'admin' ? 'text-red-500' :
                                                msg.role === 'mod' ? 'text-blue-400' :
                                                    'text-gray-300'
                                                } cursor-pointer hover:underline`}
                                            onClick={() => handleReply(msg)}
                                        >
                                            {msg.userName}
                                        </span>
                                        {msg.role === 'admin' && <span className="bg-red-600 text-white text-[10px] px-1 rounded font-bold">ADMIN</span>}
                                        {msg.role === 'mod' && <span className="bg-blue-600 text-white text-[10px] px-1 rounded font-bold">MOD</span>}
                                    </div>
                                )}

                                {/* Bubble Container (Relative for Menu Positioning) */}
                                <div className="relative group/bubble w-fit">

                                    {/* Actions (Menu/Reply) - Absolute Positioned */}
                                    <div
                                        className={`absolute top-1/2 -translate-y-1/2 z-10 opacity-0 group-hover/bubble:opacity-100 transition-opacity flex items-center gap-1 ${isMe ? 'right-full mr-2 justify-end' : 'left-full ml-2 justify-start'
                                            }`}
                                    >
                                        {!isMe && (
                                            <button
                                                onClick={() => handleReply(msg)}
                                                className="text-gray-400 hover:text-white p-1"
                                                title="Trả lời"
                                            >
                                                <FaReply size={10} />
                                            </button>
                                        )}

                                        {(isMe || currentUser?.role === 'admin' || currentUser?.role === 'mod') && (
                                            <div className="relative">
                                                <button
                                                    onClick={() => setActiveMessageMenu(activeMessageMenu === msg.id ? null : msg.id)}
                                                    className="message-menu-btn text-gray-500 hover:text-white p-1"
                                                    title="Tùy chọn"
                                                >
                                                    <FaEllipsisV size={12} />
                                                </button>
                                                {activeMessageMenu === msg.id && (
                                                    <div className={`message-menu-dropdown absolute top-full ${isMe ? 'right-0' : 'left-0'} mt-1 bg-[#2a2a2e] border border-gray-700 rounded-lg shadow-xl z-20 w-28 overflow-hidden`}>
                                                        <button
                                                            onClick={() => handleDeleteMessage(msg.id)}
                                                            className="w-full text-left px-3 py-2 text-red-400 hover:bg-white/5 text-xs flex items-center gap-2"
                                                        >
                                                            <FaTrash /> Xoá
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Bubble */}
                                    <div className={`relative px-3 py-2 text-sm shadow-sm
                                        ${isMe
                                            ? `bg-blue-600 text-white ${isGroupStart && isGroupEnd ? 'rounded-2xl' : isGroupStart ? 'rounded-2xl rounded-br-sm' : isGroupEnd ? 'rounded-2xl rounded-tr-sm' : 'rounded-2xl rounded-tr-sm rounded-br-sm'}`
                                            : `bg-[#2a2a2e] text-gray-200 border border-gray-700/50 ${isGroupStart && isGroupEnd ? 'rounded-2xl' : isGroupStart ? 'rounded-2xl rounded-bl-sm' : isGroupEnd ? 'rounded-2xl rounded-tl-sm' : 'rounded-2xl rounded-tl-sm rounded-bl-sm'}`
                                        }
                                    `}>
                                        {/* Reply Preview inside Bubble */}
                                        {msg.replyTo && (
                                            <div className={`mb-2 text-xs border-l-2 pl-2 ${isMe ? 'border-white/30 text-white/80' : 'border-gray-500 text-gray-400'}`}>
                                                <div className="font-bold mb-0.5 flex items-center gap-1">
                                                    <span>{msg.replyTo.userName}</span>
                                                    {msg.replyTo.role === 'admin' && <span className="text-[9px] border border-current px-0.5 rounded">ADMIN</span>}
                                                    {msg.replyTo.role === 'mod' && <span className="text-[9px] border border-current px-0.5 rounded">MOD</span>}
                                                </div>
                                                <div className="line-clamp-1 italic">{msg.replyTo.content}</div>
                                            </div>
                                        )}

                                        <div className="whitespace-pre-wrap" style={{ overflowWrap: 'anywhere' }}>
                                            {msg.content}
                                        </div>
                                    </div>
                                </div>

                                {/* Timestamp (Only show at end of group) */}
                                {isGroupEnd && (
                                    <div className={`text-[10px] text-gray-500 mt-1 ${isMe ? 'mr-1' : 'ml-1'}`}>
                                        {new Date(msg.timestamp).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit', hour12: false })}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
                <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            {!chatEnabled ? (
                <div className="p-4 bg-[#1f1f23] border-t border-gray-800 flex flex-col items-center justify-center text-center space-y-2">
                    <div className="text-gray-500 text-sm italic">
                        <FaCommentDots className="inline-block mb-1 mr-1" />
                        Chat đã bị tắt cho luồng phát này.
                    </div>
                </div>
            ) : (
                <div className="p-4 bg-[#1f1f23] border-t border-gray-800">
                    {replyingTo && (
                        <div className="flex items-center justify-between bg-[#2a2a2e] px-3 py-2 rounded-t-lg border-b border-gray-700 mb-0 animate-slideUpIn">
                            <div className="flex flex-col gap-0.5 min-w-0 flex-1 mr-2">
                                <div className="flex items-center gap-2 text-xs">
                                    <span className="text-gray-500">Đang trả lời:</span>
                                    <span className="font-bold text-gray-300">{replyingTo.userName}</span>
                                </div>
                                <div className="text-xs text-gray-400 truncate italic border-l-2 border-gray-600 pl-2">
                                    {replyingTo.content}
                                </div>
                            </div>
                            <button
                                onClick={() => setReplyingTo(null)}
                                className="text-gray-500 hover:text-white p-1 hover:bg-white/10 rounded transition-colors"
                            >
                                <FaTimes />
                            </button>
                        </div>
                    )}
                    <form onSubmit={handleSendMessage} className="relative">
                        <input
                            type="text"
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            placeholder={currentUser ? "Chat gì đó..." : "Đăng nhập để chat..."}
                            disabled={!currentUser}
                            className="w-full bg-[#2a2a2e] text-white rounded-xl pl-4 pr-12 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 border border-transparent placeholder-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">

                            <button
                                type="submit"
                                disabled={!inputValue.trim() || !currentUser}
                                className="p-1.5 text-blue-500 hover:text-blue-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors rounded-full hover:bg-blue-500/10"
                            >
                                <FaPaperPlane />
                            </button>
                        </div>
                    </form>

                    <div className="mt-2 flex justify-between text-xs text-gray-500">
                        {!currentUser && <span className="text-yellow-500 cursor-pointer hover:underline">Bạn cần đăng nhập để chat</span>}
                    </div>
                </div>
            )}
        </div>
    );
};

export default LiveChatSidebar;

